import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../../components/index";
import type { ModelInfo, Attachment, InferenceStatus } from "./types";
import VoiceInput from "./VoiceInput";
import { useLifeSelection, SelectionChip } from "../life";
import { getRuntimeInferenceModeLabel } from "../../../shared/consumer-language";

interface InputBarProps {
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  streaming: boolean;
  model: string;
  onModelChange: (model: string) => void;
  availableModels: ModelInfo[];
  sessionTotalTokens: number;
  sessionTotalCost: number;
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  inferenceStatus?: InferenceStatus | null;
  placeholder?: string;
  allowAttachments?: boolean;
  showModelSelector?: boolean;
  showInferenceStatus?: boolean;
  showKeyboardHint?: boolean;
}

export default function InputBar({
  input,
  onInputChange,
  onSend,
  onStop,
  streaming,
  model,
  onModelChange,
  availableModels,
  sessionTotalTokens: _sessionTotalTokens,
  sessionTotalCost: _sessionTotalCost,
  attachments,
  onAttachmentsChange,
  inferenceStatus,
  placeholder = "Message your agent...",
  allowAttachments = true,
  showModelSelector = false,
  showInferenceStatus = false,
  showKeyboardHint = true,
}: InputBarProps) {
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedModelName =
    model === "auto" ? "Auto" : availableModels.find((m) => m.id === model)?.name || model;
  const inferenceLabel = inferenceStatus
    ? getRuntimeInferenceModeLabel(inferenceStatus.mode)
    : null;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend],
  );

  const handleVoiceTranscript = useCallback(
    (text: string) => {
      onInputChange(input ? `${input} ${text}` : text);
      textareaRef.current?.focus();
    },
    [input, onInputChange],
  );

  // File attachment handling
  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const newAttachments: Attachment[] = [];

      for (const file of Array.from(files)) {
        const id = `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        let type: Attachment["type"] = "other";
        if (file.type.startsWith("image/")) type = "image";
        else if (file.type === "application/pdf") type = "pdf";
        else if (
          file.type.startsWith("text/") ||
          file.name.match(/\.(md|json|csv|ts|tsx|js|jsx|py|rs|go)$/)
        )
          type = "text";

        const att: Attachment = { id, file, type };

        // Generate preview for images
        if (type === "image") {
          const reader = new FileReader();
          reader.onload = (e) => {
            att.preview = e.target?.result as string;
            onAttachmentsChange([...attachments, ...newAttachments]);
          };
          reader.readAsDataURL(file);
        }

        newAttachments.push(att);
      }

      onAttachmentsChange([...attachments, ...newAttachments]);
    },
    [attachments, onAttachmentsChange],
  );

  const removeAttachment = useCallback(
    (id: string) => {
      onAttachmentsChange(attachments.filter((a) => a.id !== id));
    },
    [attachments, onAttachmentsChange],
  );

  // Drag-and-drop
  const [dragOver, setDragOver] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Paste images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        const dt = new DataTransfer();
        files.forEach((f) => dt.items.add(f));
        handleFileSelect(dt.files);
      }
    };

    const el = textareaRef.current;
    el?.addEventListener("paste", handlePaste);
    return () => el?.removeEventListener("paste", handlePaste);
  }, [handleFileSelect]);

  // Close model selector on outside click
  useEffect(() => {
    if (!modelSelectorOpen) return;
    const handleClick = () => setModelSelectorOpen(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [modelSelectorOpen]);

  // Focus textarea on Cmd+K
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, []);

  const { selections, removeSelection } = useLifeSelection();

  return (
    <div className="shrink-0 px-4 md:px-6 pb-4 pt-2">
      <div className="max-w-3xl mx-auto">
        <div
          className="rounded-xl overflow-hidden transition-colors"
          style={{
            backgroundColor: "var(--c-surface)",
            border: `1px solid ${dragOver ? "var(--c-primary)" : "var(--c-border)"}`,
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Life selection chips */}
          {selections.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 pt-2 flex-wrap">
              {selections.map((sel) => (
                <SelectionChip key={sel.itemId} selection={sel} onRemove={removeSelection} />
              ))}
            </div>
          )}

          {/* Attachment previews */}
          {attachments.length > 0 && (
            <div className="flex items-center gap-2 px-3 pt-2 flex-wrap">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]"
                  style={{
                    backgroundColor: "var(--c-surface-2)",
                    color: "var(--c-text-secondary)",
                  }}
                >
                  {att.type === "image" && att.preview ? (
                    <img src={att.preview} alt="" className="w-5 h-5 rounded object-cover" />
                  ) : (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M7 1H3a1 1 0 00-1 1v8a1 1 0 001 1h6a1 1 0 001-1V4L7 1z" />
                    </svg>
                  )}
                  <span className="max-w-[100px] truncate">{att.file.name}</span>
                  <button
                    onClick={() => removeAttachment(att.id)}
                    className="ml-0.5 hover:opacity-70"
                    style={{ color: "var(--c-text-faint)" }}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M2 2l6 6M8 2l-6 6" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={streaming}
            className="w-full px-4 pt-3 pb-1 text-sm resize-none focus:outline-none disabled:opacity-50"
            style={{
              backgroundColor: "transparent",
              color: "var(--c-text)",
              minHeight: 44,
              maxHeight: 200,
            }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 200) + "px";
            }}
          />

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center gap-2">
              {showInferenceStatus && inferenceStatus && inferenceLabel && (
                <div
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px]"
                  title={inferenceStatus.reason}
                  style={{
                    color: inferenceStatus.isFallback
                      ? "var(--c-warning)"
                      : "var(--c-text-secondary)",
                    backgroundColor: inferenceStatus.isFallback
                      ? "rgba(234, 179, 8, 0.12)"
                      : "var(--c-surface-2)",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "999px",
                      backgroundColor: inferenceStatus.isFallback
                        ? "var(--c-warning)"
                        : "var(--c-success)",
                    }}
                  />
                  <span>{inferenceLabel}</span>
                </div>
              )}

              {showModelSelector && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setModelSelectorOpen(!modelSelectorOpen)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors"
                    style={{ color: "var(--c-text-faint)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <circle cx="6" cy="6" r="3" />
                    </svg>
                    {selectedModelName}
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M3 4l2 2 2-2" />
                    </svg>
                  </button>
                  {modelSelectorOpen && (
                    <div
                      className="absolute bottom-full left-0 mb-1 py-1 rounded-lg shadow-lg z-50 min-w-[180px]"
                      style={{
                        backgroundColor: "var(--c-surface)",
                        border: "1px solid var(--c-border)",
                      }}
                    >
                      <button
                        className="w-full text-left px-3 py-1.5 text-xs transition-colors"
                        style={{
                          color: model === "auto" ? "var(--c-primary)" : "var(--c-text-secondary)",
                          backgroundColor: model === "auto" ? "var(--c-primary-subtle)" : undefined,
                        }}
                        onMouseEnter={(e) => {
                          if (model !== "auto")
                            e.currentTarget.style.backgroundColor = "var(--c-hover)";
                        }}
                        onMouseLeave={(e) => {
                          if (model !== "auto") e.currentTarget.style.backgroundColor = "";
                        }}
                        onClick={() => {
                          onModelChange("auto");
                          setModelSelectorOpen(false);
                        }}
                      >
                        Auto
                      </button>
                      {availableModels.map((m) => (
                        <button
                          key={m.id}
                          className="w-full text-left px-3 py-1.5 text-xs transition-colors"
                          style={{
                            color: model === m.id ? "var(--c-primary)" : "var(--c-text-secondary)",
                            backgroundColor: model === m.id ? "var(--c-primary-subtle)" : undefined,
                          }}
                          onMouseEnter={(e) => {
                            if (model !== m.id)
                              e.currentTarget.style.backgroundColor = "var(--c-hover)";
                          }}
                          onMouseLeave={(e) => {
                            if (model !== m.id) e.currentTarget.style.backgroundColor = "";
                          }}
                          onClick={() => {
                            onModelChange(m.id);
                            setModelSelectorOpen(false);
                          }}
                        >
                          {m.name || m.id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {allowAttachments && (
                <>
                  {/* Attachment button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={streaming}
                    className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                    style={{ color: "var(--c-text-faint)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                    title="Attach file"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M14 8l-5.5 5.5a3.5 3.5 0 01-5-5L9 3a2.5 2.5 0 013.5 3.5L7 12a1.5 1.5 0 01-2-2l5.5-5.5" />
                    </svg>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                </>
              )}

              {/* Session stats removed — don't emphasize cost to users */}
            </div>

            <div className="flex items-center gap-1">
              {/* Voice input */}
              <VoiceInput onTranscript={handleVoiceTranscript} disabled={streaming} />

              {/* Send / Stop button */}
              {streaming ? (
                <Button variant="danger" size="sm" onClick={onStop}>
                  Stop
                </Button>
              ) : (
                <button
                  onClick={onSend}
                  disabled={!input.trim() && attachments.length === 0}
                  className="p-1.5 rounded-lg transition-all duration-200 disabled:opacity-30"
                  style={{
                    backgroundColor:
                      input.trim() || attachments.length > 0 ? "var(--c-primary)" : undefined,
                    color:
                      input.trim() || attachments.length > 0
                        ? "var(--c-primary-text)"
                        : "var(--c-text-faint)",
                  }}
                  title="Send (Enter)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M2 8h12M10 4l4 4-4 4" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {showKeyboardHint && (
          <div className="flex justify-center mt-1">
            <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
              <kbd className="font-mono">Enter</kbd> to send,{" "}
              <kbd className="font-mono">Shift+Enter</kbd> for newline
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
